import React from 'react';
import authClient from 'auth-client';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { NavLink, Switch, Route, Redirect } from 'react-router-dom';

import { CPage, CTab, CButton } from '@appcues/component-library';
import { Icon } from 'next/components/ui';
import { API_KEYS, MOBILE_FLOWS } from 'next/entities/features';
import { selectAccountFeature } from 'reducers/account/features';

import IndexPage from 'components/Common/IndexPage';
import Installation from 'components/Installation';
import NextInstallation from 'components/Installation/NextInstallation';
import Team from 'components/Team';
import Subscription from 'components/Subscription';
import AccountPage from 'components/AccountPage';
import IntegrationsPage from 'components/IntegrationsPage';
import PropertiesAndEvents from 'components/Settings/PropertiesAndEvents';
import Localization from 'components/Settings/Localization';
import ApiKeys from 'components/Settings/keys/ApiKeys';
import ApiKeysTab from 'components/Settings/ApiKeysTab';

import { reportError } from 'helpers/error-reporting';
import { FLOW_LOCALIZATION } from 'constants/features';
import { logoutUserFromChromeExtension } from 'actions/chromeExtension';

// This is a temporary hack to make old page headers look like new one
const SecondBar = styled(CPage.SecondBar)`
  position: sticky;
  top: 106px;
  z-index: 3000;
`;

export function Settings({
  hasAPIKeys,
  hasFlowLocalization,
  hasMobileFlows,
  logoutUserFromChromeExtension: logoutFromCrx,
}) {
  const handleClickLogout = () => {
    logoutFromCrx();
    authClient.logoutUser({ reportError });
  };

  return (
    <IndexPage
      title="Settings"
      subHeaderActions={
        <CButton onClick={handleClickLogout}>
          <Icon icon="sign-out-alt" />
          {'\u00A0'} Sign out
        </CButton>
      }
    >
      <SecondBar>
        <CTab.Bar>
          <CTab is={NavLink} to="/settings/account" id="account">
            Account
          </CTab>
          <CTab is={NavLink} to="/settings/installation" id="installation">
            {hasMobileFlows ? 'Apps & Installation' : 'Installation'}
          </CTab>
          <CTab is={NavLink} to="/settings/integrations" id="integrations">
            Integrations
          </CTab>
          <CTab is={NavLink} to="/settings/events" id="events">
            Events and properties
          </CTab>
          <CTab is={NavLink} to="/settings/team" id="team">
            Team
          </CTab>
          <CTab is={NavLink} to="/settings/subscription" id="subscription">
            Subscription
          </CTab>
          {hasFlowLocalization && (
            <CTab is={NavLink} to="/settings/localization" id="localization">
              Localization
            </CTab>
          )}
          <ApiKeysTab hasAPIKeys={hasAPIKeys} />
        </CTab.Bar>
      </SecondBar>
      <CPage.Container>
        <Switch>
          <Route path="/settings/account" component={AccountPage} />
          <Route
            path="/settings/installation"
            component={hasMobileFlows ? NextInstallation : Installation}
          />
          <Route path="/settings/integrations" component={IntegrationsPage} />
          <Route path="/settings/events" component={PropertiesAndEvents} />
          <Route path="/settings/team" component={Team} />
          <Route path="/settings/subscription" component={Subscription} exact />

          {hasFlowLocalization && (
            <Route path="/settings/localization" component={Localization} />
          )}
          {hasAPIKeys && <Route path="/settings/keys" component={ApiKeys} />}
          <Redirect from="/settings/install" to="/settings/installation" />
          <Redirect exact from="/settings" to="/settings/account" />
        </Switch>
      </CPage.Container>
    </IndexPage>
  );
}

Settings.propTypes = {
  hasAPIKeys: PropTypes.bool,
  hasFlowLocalization: PropTypes.bool,
  hasMobileFlows: PropTypes.bool,
  logoutUserFromChromeExtension: PropTypes.func,
};

const mapStateToProps = state => ({
  hasAPIKeys: selectAccountFeature(state, API_KEYS),
  hasFlowLocalization: selectAccountFeature(state, FLOW_LOCALIZATION),
  hasMobileFlows: selectAccountFeature(state, MOBILE_FLOWS),
});

const mapDispatchToProps = {
  logoutUserFromChromeExtension,
};

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
